import {Navigate, Route, Routes, useParams} from 'react-router';

import {AccountTypeT} from '@/lib/types/authentication.types';

import Error404Page from '@/pages/(errors)/404/error404Page';
import {AuthCallbackPage} from '@/pages/(public)/pages/authCallback/authCallback';
import RegistrationPage from '@/pages/(public)/pages/register/registrationPage';
import ResetPasswordPage from '@/pages/(public)/pages/resetPassword/resetPasswordPage';
import SignInPage from '@/pages/(public)/pages/signIn/signInPage';
import SignUpPage from '@/pages/(public)/pages/signUp/signUpPage';
import BrandDetailsPage from '@/pages/(shared)/brands/detail/brandDetailsPage';
import {DistributorDetailsPage} from '@/pages/(shared)/distributor/detail/distributorDetailsPage';
import InvoiceDetailsPage from '@/pages/(shared)/invoices/detail/invoiceDetailsPage';
import {MarketDetailsPage} from '@/pages/(shared)/market/marketDetailsPage';
import OrderDetailsPage from '@/pages/(shared)/orders/detail/orderDetailsPage';
import TransferManifestDetailsPage from '@/pages/(shared)/orders/detail/transferManifest/transferManifestDetailsPage';
import PaymentStatus from '@/pages/(shared)/payments/successFailure';
import CreateMarketPositionsPage from '@/pages/(shared)/positions/new/createMarketPositionsPage';
import CreateWholesalePositionsPage from '@/pages/(shared)/positions/new/createWholesalePositionsPage';
import ProductDetailsPage from '@/pages/(shared)/products/detail/productDetailsPage';
import {RetailerDetailsPage} from '@/pages/(shared)/retailers/detail/retailerDetailsPage';
import ButtonsDocPage from '@/pages/_dev/buttons/page';
import IconPreviewPage from '@/pages/_dev/icons/page';
import PersistentNotificationsDebugPage from '@/pages/_dev/notifications/persistent/PersistentNotificationsDebugPage';
import PersistentNotificationsExamplesPage from '@/pages/_dev/notifications/persistent/PersistentNotificationsExamplesPage';
import PersistentNotificationsGeneratorPage from '@/pages/_dev/notifications/persistent/PersistentNotificationsGeneratorPage';
import PersistentNotificationsPropsPage from '@/pages/_dev/notifications/persistent/PersistentNotificationsPropsPage';
import PersistentNotificationsUsagePage from '@/pages/_dev/notifications/persistent/PersistentNotificationsUsagePage';
import NotificationSystemOverviewPage from '@/pages/_dev/notifications/system/NotificationSystemOverviewPage';
import ToastExamplesPage from '@/pages/_dev/notifications/toasts/ToastExamplesPage';
import ToastGeneratorPage from '@/pages/_dev/notifications/toasts/ToastGeneratorPage';
import ToastPropsPage from '@/pages/_dev/notifications/toasts/ToastPropsPage';
import ToastUsagePage from '@/pages/_dev/notifications/toasts/ToastUsagePage';
import DevHomePage from '@/pages/_dev/page';
import AdminAdmins from '@/pages/admins/admins/adminAdmins';
import AdminApplications from '@/pages/admins/applications/applicationsPage';
import AdminBrands from '@/pages/admins/brands/adminBrands';
import AdminDashboard from '@/pages/admins/dashboard/adminDashboard';
import AdminDistributors from '@/pages/admins/distributors/adminDistributors';
import CreateInvoicePage from '@/pages/admins/invoices/createInvoice/createInvoicePage';
import AdminInvoicesPage from '@/pages/admins/invoices/invoicesPage';
import ItemsSummaryPage from '@/pages/admins/items/itemsPage';
import AdminMarketsPage from '@/pages/admins/markets/adminMarketsPage';
import AdminMetrcPage from '@/pages/admins/metrc/adminMetrcPage';
import AdminOrders from '@/pages/admins/orders/adminOrders';
import CreateOrderPage from '@/pages/admins/orders/new/createOrderPage';
import AdminPositions from '@/pages/admins/positions/adminPositions';
import AdminProducts from '@/pages/admins/products/adminProducts';
import AdminRetailers from '@/pages/admins/retailers/adminRetailers';
import AdminSpeculators from '@/pages/admins/speculators/adminSpeculators';
import AdminSpeculatorDetails from '@/pages/admins/speculators/detail/speculatorDetails';
import AdminTransactionsPage from '@/pages/admins/transactions/adminTransactionsPage';
import DistributorDashboard from '@/pages/distributors/dashboard/distributorDashboard';
import DistributorOrdersPage from '@/pages/distributors/orders/distributorOrdersPage';
import CreateDistributorOrderPage from '@/pages/distributors/orders/new/createDistributorOrder';
import DistributorRateSheetsPage from '@/pages/distributors/rateSheets/distributorRateSheets';
import CreateRateSheetPage from '@/pages/distributors/rateSheets/new/createRateSheetPage';
import RetailerDashboard from '@/pages/retailers/dashboard/retailerDashboard';
import RetailerInvoicesPage from '@/pages/retailers/invoices/retailerInvoicesPage';
import CreateRetailOrderPage from '@/pages/retailers/orders/new/createRetailOrderPage';
import RetailerOrdersPage from '@/pages/retailers/orders/retailerOrdersPage';
import SpeculatorBrandsPage from '@/pages/speculators/brands/speculatorBrandsPage';
import SpeculatorDashboard from '@/pages/speculators/dashboard/speculatorDashboard';
import SpeculatorItems from '@/pages/speculators/items/speculatorItems';
import SpeculatorLeaderboard from '@/pages/speculators/leaderboard/speculatorLeaderboard';
import NotificationsPage from '@/pages/speculators/notifications/page';
import PositionDetailsPage from '@/pages/speculators/positions/detail/positionDetailsPage';
import SpeculatorPositionsPage from '@/pages/speculators/positions/speculatorPositionsPage';
import SpeculatorProducts from '@/pages/speculators/products/speculatorProducts';
import SpeculatorReports from '@/pages/speculators/reports/speculatorReports';
import SpeculatorSettingsPage from '@/pages/speculators/settings/speculatorSettingsPage';
import UpdatePasswordPage from '@/pages/speculators/settings/update-password/UpdatePasswordPage';

import {useAuth} from '@/providers/components/authProvider';

import AppLayout from '@/components/layouts/AppLayout';
import BaseLayout from '@/components/layouts/BaseLayout';
import ProtectedRoutes from '@/components/router/components/ProtectedRoutes';
import ScrollToTopOnPathChange from '@/components/scrollToTopOnPathChange';

const AppRouter = () => {
  const {from} = useParams<{from: AccountTypeT | undefined}>();
  const {userAccounts, isAdministrator, isSuperAdmin, isDistributor, isAuthenticated, isRetailer} = useAuth();

  return (
    <>
      <Routes>
        {/* Public Routes */}
        <Route element={<BaseLayout />}>
          {/* Single root route with logic */}
          <Route
            path="/"
            index
            element={
              !isAuthenticated ? (
                <Navigate to="/sign-in" replace state={{from}} />
              ) : userAccounts?.verification?.is_new_user ? (
                <Navigate to="/register-details" replace state={{from}} />
              ) : isAdministrator ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <Navigate to="/speculator/dashboard" replace />
              )
            }
          />
          {/* @ts-expect-error = TEMPORARY IGNORE */}
          <Route path="/sign-in" element={<SignInPage />} />
          {/* @ts-expect-error = TEMPORARY IGNORE */}
          <Route path="/sign-in/:from?" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/sign-up/:from?" element={<SignUpPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register-details" element={<RegistrationPage />} />
          <Route path="/cancel" element={<PaymentStatus />} />
          <Route path="/success" element={<PaymentStatus />} />
          <Route path={import.meta.env.VITE_AUTH_CALLBACK_URL} element={<AuthCallbackPage />} />
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoutes />}>
          {/* All Other App Routes */}
          <Route element={<AppLayout />}>
            {/* Development Routes - Only available in development */}
            {import.meta.env.VITE_APP_MODE === 'development' && (
              <Route path="_dev">
                <Route index element={<DevHomePage />} />
                {/* Buttons & Icons Section */}
                <Route path="buttons">
                  <Route index element={<Navigate to="buttons/examples" replace />} />
                  <Route path=":category" element={<ButtonsDocPage />} />
                  <Route path=":category/:tab" element={<ButtonsDocPage />} />
                </Route>
                {/* Icons Section */}
                <Route path="icons">
                  <Route index element={<Navigate to="browse" replace />} />
                  <Route path=":tab" element={<IconPreviewPage />} />
                </Route>
                {/* Notifications Section */}
                <Route path="notifications">
                  <Route index element={<Navigate to="system/overview" replace />} />
                  <Route path="system">
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<NotificationSystemOverviewPage />} />
                  </Route>
                  <Route path="toasts">
                    <Route index element={<Navigate to="examples" replace />} />
                    <Route path="examples" element={<ToastExamplesPage />} />
                    <Route path="props" element={<ToastPropsPage />} />
                    <Route path="usage" element={<ToastUsagePage />} />
                    <Route path="generator" element={<ToastGeneratorPage />} />
                  </Route>
                  <Route path="persistent">
                    <Route index element={<Navigate to="examples" replace />} />
                    <Route path="examples" element={<PersistentNotificationsExamplesPage />} />
                    <Route path="props" element={<PersistentNotificationsPropsPage />} />
                    <Route path="usage" element={<PersistentNotificationsUsagePage />} />
                    <Route path="generator" element={<PersistentNotificationsGeneratorPage />} />
                    <Route path="debug" element={<PersistentNotificationsDebugPage />} />
                  </Route>
                </Route>
              </Route>
            )}

            {/* Speculator Routes */}
            <Route path="/speculator">
              <Route index element={<Navigate to="/speculator/dashboard" replace />} />
              <Route path="dashboard" element={<SpeculatorDashboard />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="positions">
                <Route index element={<SpeculatorPositionsPage />} />
                <Route path="new">
                  <Route index element={<Navigate to="market" replace />} />
                  {isAdministrator && <Route path="wholesale" element={<CreateWholesalePositionsPage />} />}
                  <Route path="market" element={<CreateMarketPositionsPage />} />
                </Route>
              </Route>
              <Route path="products" element={<SpeculatorProducts />} />
              <Route path="brands" element={<SpeculatorBrandsPage />} />
              <Route path="items" element={<SpeculatorItems />} />
              <Route path="settings" element={<SpeculatorSettingsPage />} />
              <Route path="settings/update-password" element={<UpdatePasswordPage />} />
              <Route path="reports" element={<SpeculatorReports />} />
              <Route path="leaderboard" element={<SpeculatorLeaderboard />} />
            </Route>

            {/* Admin Routes */}
            {isAdministrator && (
              <Route path="/admin">
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="speculators" element={<AdminSpeculators />} />
                <Route path="speculators/:id" element={<AdminSpeculatorDetails />} />
                <Route path="distributors" element={<AdminDistributors />} />
                <Route path="markets" element={<AdminMarketsPage />} />
                <Route path="brands" element={<AdminBrands />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="positions">
                  <Route index element={<AdminPositions />} />
                  out
                  <Route path="new">
                    <Route index element={<Navigate to="market" replace />} />
                    {isAdministrator && <Route path="wholesale" element={<CreateWholesalePositionsPage />} />}
                    <Route path="market" element={<CreateMarketPositionsPage />} />
                  </Route>
                </Route>
                <Route path="retailers" element={<AdminRetailers />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="orders/new" element={<CreateOrderPage />} />
                <Route path="orders/:orderId" element={<OrderDetailsPage />} />
                <Route path="orders/:id/transfer-manifest/:transfer_id" element={<TransferManifestDetailsPage />} />
                <Route path="payment-methods" element={<h1>This is payment methods section</h1>} />
                <Route path="settings" element={<h1>This is settings section</h1>} />
                <Route path="reports" element={<h1>This is reports section</h1>} />
                <Route path="items" element={<ItemsSummaryPage />} />
                <Route path="transactions" element={<AdminTransactionsPage />} />
                <Route path="invoices" element={<AdminInvoicesPage />} />
                <Route path="invoices/create/:type/:id" element={<CreateInvoicePage />} />
                <Route path="invoices/:invoiceId" element={<InvoiceDetailsPage />} />
                <Route path="applications" element={<AdminApplications />} />
                {/* Super Admin Routes */}
                {isSuperAdmin && <Route path="admins" element={<AdminAdmins />} />}
                <Route path="metrc" element={<AdminMetrcPage />} />
              </Route>
            )}

            {/* Distributor Routes */}
            {isDistributor && (
              <Route path="/distributor">
                <Route index element={<DistributorDashboard />} />
                <Route path="rate-sheets" element={<DistributorRateSheetsPage />} />
                <Route path="rate-sheets/new" element={<CreateRateSheetPage />} />
                <Route path="rate-sheets/:rateSheetId" element={<CreateRateSheetPage />} />
                <Route path="rate-sheets/:rateSheetId/edit" element={<CreateRateSheetPage />} />
                <Route path="orders" element={<DistributorOrdersPage />} />
                <Route path="orders/new" element={<CreateDistributorOrderPage />} />
                <Route path="orders/:distributorId/new" element={<CreateDistributorOrderPage />} />
                <Route path="orders/:orderId" element={<OrderDetailsPage />} />
              </Route>
            )}

            {/* Retailer Routes */}
            {isRetailer && (
              <Route path="/retailer">
                <Route index element={<RetailerDashboard />} />
                <Route path="orders" element={<RetailerOrdersPage />} />
                <Route path="orders/new" element={<CreateRetailOrderPage />} />
                <Route path="orders/:retailerId/new" element={<CreateRetailOrderPage />} />
                <Route path="orders/:orderId" element={<OrderDetailsPage />} />
                <Route path="invoices" element={<RetailerInvoicesPage />} />
                <Route path="invoices/:invoiceId" element={<InvoiceDetailsPage />} />
              </Route>
            )}

            {/* Shared Detail Routes */}
            <Route path="/brands/:id" element={<BrandDetailsPage />} />
            <Route path="/products/:id" element={<ProductDetailsPage />} />
            <Route path="/distributors/:id" element={<DistributorDetailsPage />} />
            <Route path="/retailers/:id" element={<RetailerDetailsPage />} />
            <Route path="/markets/:id" element={<MarketDetailsPage />} />
            <Route path="/positions/:id" element={<PositionDetailsPage />} />
          </Route>

          {/* Catch-all route */}
          <Route path="*" element={<Error404Page />} />
        </Route>
      </Routes>
      <ScrollToTopOnPathChange />
    </>
  );
};

export default AppRouter;
